// Interfaces
import { ITier } from "@/Common/helpers/Matrices/interfaces";
import { TOptionIdentifier, TTierBonus } from "./interfaces";

// Helpers
import { isLastValid, isValidMatrixRange } from "@/Common/helpers/Matrices/tiers";

const isTierConditionsValid = (args: { isSomeConfigured: boolean; tiers: TTierBonus[] }) => {
	const { isSomeConfigured, tiers = [] } = args ?? {};
	const isLastBonusValid = isLastValid(tiers as unknown as ITier[], { column: "bonuses", reference: "bonus" });
	const isMatrixInRange = isValidMatrixRange(tiers as unknown as ITier[], { column: "bonuses" });
	return isSomeConfigured || (isLastBonusValid && isMatrixInRange);
};

const isCommissionValid = (args: { isSomeConfigured: boolean; tiers: TTierBonus[] }) => {
	const { isSomeConfigured, tiers = [] } = args ?? {};
	return isSomeConfigured || isLastValid(tiers as unknown as ITier[], { column: "bonuses", reference: "bonus" });
};

const validateElrHroTiers = (innerTiers: TTierBonus[], option?: TOptionIdentifier, isSomeConfigured = false) => {
	return [
		{
			definition: {
				useKey: "rows",
				operators: {
					conditions: [
						{
							type: "INLINE",
							on: "value",
							compare: "<", // If from < to is good,
						},
						{
							type: "STEP",
							on: "value",
							compare: "===",
							deltas: { Percent: 0.01, Dollar: 0.01, Unit: 0 }, // Add delta on upper value
							distinct: 0,
						},
					],
				},
				errorsMap: ["from", "to"],
				message: "Invalid tiers",
				validationType: "DOUBLE_INPUT",
				specialProperties: {
					noFirstError: true,
					noLastError: true,
				},
			},
			section: innerTiers || [],
		},
		{
			definition: {
				useKey: "header",
				operators: {
					conditions: [
						{
							type: "INLINE",
							on: "value",
							compare: "<", // If from < to is good,
						},
						{
							type: "STEP",
							on: "value",
							compare: "===",
							deltas: { Percent: 0.01, Dollar: 0.01, Unit: 0 }, // Add delta on upper value
						},
					],
				},
				errorsMap: ["from", "to"],
				message: `Invalid ${option ?? "gross"} tiers`,
				validationType: "DOUBLE_INPUT",
				specialProperties: {
					noFirstError: true,
					noLastError: true,
				},
			},
			section: innerTiers?.[0]?.bonuses || [],
		},
		{
			definition: {
				useKey: "bonuses",
				operators: {
					conditions: [
						{
							order: "ROWS",
							type: "STEP",
							on: "value",
							compare: "<=", // If current <= next is good,
						},
						{
							order: "COLUMNS",
							type: "STEP",
							on: "value",
							compare: "<",
							deltas: { Dollar: 0, Percent: 0 },
						},
					],
				},
				errorsMap: ["bonus"],
				errorsMapOn: "bonuses",
				message: "Incorrect bonuses definition",
				validationType: "MATRIX",
			},
			section: innerTiers,
		},
		{
			definition: {
				useKey: "range",
				validationType: "CUSTOM",
				errorsMap: ["tiers"],
				message: "Please add at least a two tiers configuration",
				operators: {
					customFn: (params?: { checkTiers: ITier[]; someConfigured: boolean }) => {
						const { checkTiers = [], someConfigured = true } = params ?? {};
						if (someConfigured) {
							return [{ tiers: false }];
						}
						const isValidRange = isValidMatrixRange(checkTiers, { column: "bonuses" });
						return [{ tiers: !isValidRange }];
					},
					params: {
						checkTiers: innerTiers,
						someConfigured: isSomeConfigured,
					},
				},
			},
			section: [{ tiers: false }],
		},
	];
};

export { validateElrHroTiers, isTierConditionsValid, isCommissionValid };
